// Third party imports
import React from "react";

// Utils and providers
import constants from "constants/constants";
import withComponentLibraryTheme from "hocs/withComponentLibraryTheme";
import { msg_text } from "../../ReferralsTableConfig";
import { is_empty } from "utils/validations";
import { useNotifications } from "utils/hooks";
import { appendUrlParams } from "utils/urlUtils";
import { onScrollToDiv } from "features/Common/utils/common.utils";
import { getUserCurrencySymbol } from "utils/AuthUtil";
import { roundOff } from "utils/Utils";
import { REFERRAL_HOW_IT_WORKS_CONSTANTS } from "../ReferralHowItWorks/ReferralHowItWorks.constants";
import ExlyImage from "features/Common/modules/ExlyImage/ExlyImage";

// Social Icons SVGs
import Email from "assets/vectors/social/emailRounded.svg";
import WhatsApp from "assets/vectors/social/whatsapp.svg";
import Twitter from "assets/vectors/social/twitterRounded.svg";
import Facebook from "assets/vectors/social/facebookRounded.svg";

import { ReferralMetricCard } from "./ReferralMetricCard";
import {
  SHARE_REFERRALS_PROPS,
  SHARE_REFERRALS_DEFAULT_PROPS,
} from "./ShareReferrals.types";

// Main Component
const ShareReferralsBase = ({
  link,
  sharedLink,
  referralOverview,
  openModal,
}) => {
  const { pushNotification } = useNotifications();

  const handleCopy = () => {
    if (navigator && navigator.clipboard && link) {
      navigator.clipboard.writeText(link);
      pushNotification("Link Copied!", {
        variant: "outlined",
        color: "primary",
      });
    }
  };

  const handleScrollToHowItWorks = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    onScrollToDiv({
      elementId: REFERRAL_HOW_IT_WORKS_CONSTANTS.SECTION_ID,
      block: "start",
    });
  };

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    `${msg_text} ${sharedLink || link || ""}`
  )}`;
  const whatsappUrl = `https://api.whatsapp.com/send?&text=${encodeURIComponent(
    `${msg_text} ${sharedLink || link || ""}`
  )}`;
  const facebookUrl = appendUrlParams(constants.facebook_share_url, {
    link: sharedLink || link || "",
    quote: `${msg_text} ${sharedLink || link || ""}`,
  });

  return (
    <section className="tw-w-full tw-mb-8">
      {/* Header: Title + How does it work? link */}
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
        <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-slate-900 tw-m-0">
          Use referrals
        </h2>
        <button
          type="button"
          onClick={handleScrollToHowItWorks}
          className="tw-text-sm sm:tw-text-base tw-font-medium tw-text-[#5046E5] tw-underline tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 hover:tw-text-[#4338CA] tw-transition-colors"
        >
          How does it work?
        </button>
      </div>

      {/* Description text */}
      <p className="tw-text-sm sm:tw-text-base tw-text-slate-600 tw-leading-relaxed tw-max-w-3xl tw-m-0 tw-mb-6">
        <span className="tw-font-bold tw-text-slate-900">
          Use this link to refer your friends and earn a flat{" "}
          {referralOverview?.commission_rate ?? 25}% commission.
        </span>{" "}
        The moment they subscribe to any Exly plan, your commission lands
        straight in your wallet.
      </p>

      {/* Link Input Row + Copy + OR + Social Share Icons */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 sm:tw-gap-3.5 tw-mb-8 tw-mt-5">
        {/* Link Input Field */}
        <div className="tw-flex-1 tw-min-w-[260px] sm:tw-min-w-[380px] tw-h-12 tw-bg-[#F8F9FD] tw-border tw-border-solid tw-border-[#E0E2F0] tw-rounded-xl tw-px-4 tw-flex tw-items-center tw-overflow-hidden">
          <span className="tw-text-[#5046E5] tw-text-sm sm:tw-text-base tw-font-normal tw-truncate">
            {link || "https://creator.exlyapp.com/#/?ref=..."}
          </span>
        </div>

        {/* Copy Button */}
        <button
          type="button"
          onClick={handleCopy}
          className="tw-h-12 tw-px-7 tw-bg-[#5046E5] hover:tw-bg-[#4338CA] tw-text-white tw-font-semibold tw-text-base tw-rounded-xl tw-border-none tw-cursor-pointer tw-transition-colors tw-shadow-sm active:tw-scale-[0.98]"
        >
          Copy
        </button>

        {/* OR separator */}
        <span className="tw-text-xs sm:tw-text-sm tw-font-medium tw-text-slate-400 tw-px-1 select-none">
          OR
        </span>

        {/* Social Share Icons */}
        <div className="tw-flex tw-items-center tw-gap-2.5">
          {/* X / Twitter */}
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Twitter"
            className="tw-transition-transform hover:tw-scale-105 active:tw-scale-95 tw-inline-flex"
          >
            <ExlyImage
              src={Twitter}
              alt="Twitter"
              className="tw-w-10 tw-h-10 tw-cursor-pointer"
            />
          </a>

          {/* Facebook */}
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on Facebook"
            className="tw-transition-transform hover:tw-scale-105 active:tw-scale-95 tw-inline-flex"
          >
            <ExlyImage
              src={Facebook}
              alt="Facebook"
              className="tw-w-10 tw-h-10 tw-cursor-pointer"
            />
          </a>

          {/* WhatsApp */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share on WhatsApp"
            className="tw-transition-transform hover:tw-scale-105 active:tw-scale-95 tw-inline-flex"
          >
            <ExlyImage
              src={WhatsApp}
              alt="WhatsApp"
              className="tw-w-12 tw-h-12 tw-cursor-pointer"
            />
          </a>

          {/* Email */}
          <button
            type="button"
            onClick={openModal}
            aria-label="Share via Email"
            className="tw-p-0 tw-bg-transparent tw-border-none tw-cursor-pointer tw-transition-transform hover:tw-scale-105 active:tw-scale-95 tw-inline-flex"
          >
            <ExlyImage
              src={Email}
              alt="Email"
              className="tw-w-10 tw-h-10 tw-cursor-pointer"
            />
          </button>
        </div>
      </div>

      {/* Sample Preview Dotted Container */}
      <div className="tw-w-full tw-rounded-2xl tw-border tw-border-dashed tw-border-[#E5C388] tw-p-5 sm:tw-p-6 tw-bg-[#FCFAF6]/40">
        {/* Sample Preview Tag + Text */}
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2.5 tw-mb-5">
          <span className="tw-text-xs sm:tw-text-sm tw-text-slate-500">
            If you refer, this is where your sign-ups and total commission will
            show up.
          </span>
        </div>

        {/* 3 Metric Cards Grid */}
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 sm:tw-gap-5">
          <ReferralMetricCard
            icon="👥"
            iconLabel="Sign ups"
            title="Total Referred Sign Ups"
            value={referralOverview?.total_referrals ?? "0"}
            bgClass="tw-bg-[#F0EEFD]"
            titleClass="tw-text-[#4B5563]"
            loaderColor="#4E39DF"
            isLoading={!referralOverview}
          />

          <ReferralMetricCard
            icon="💵"
            iconLabel="Commission"
            title="Total Commissions Earned"
            value={`${getUserCurrencySymbol()}${
              !is_empty(referralOverview?.total_amount_earned)
                ? roundOff(referralOverview?.total_amount_earned, 2)
                : "0"
            }`}
            bgClass="tw-bg-[#E5F9ED]"
            titleClass="tw-text-[#087443]"
            loaderColor="#087443"
            isLoading={!referralOverview}
          />

          <ReferralMetricCard
            icon="🤝"
            iconLabel="Commission rate"
            title="Your Commission Rate"
            value={`Flat ${referralOverview?.commission_rate ?? 25}%`}
            bgClass="tw-bg-[#E8F1FD]"
            titleClass="tw-text-[#1E40AF]"
            loaderColor="#1E40AF"
            isLoading={!referralOverview}
          />
        </div>
      </div>
    </section>
  );
};

ShareReferralsBase.propTypes = SHARE_REFERRALS_PROPS;
ShareReferralsBase.defaultProps = SHARE_REFERRALS_DEFAULT_PROPS;

export const ShareReferrals = withComponentLibraryTheme(ShareReferralsBase);
